import { action, computed, makeObservable, observable } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import {
  type DocumentSectionSlug,
  isDocumentSectionSlug,
  isKnownSectionSlug,
  isTopLevelSectionSlug,
  type KnownSectionSlug,
  type OrganizationSection,
  organizationSectionItemSchema,
  organizationSectionsResponseSchema,
  SECTION_NAMES,
  type TopLevelSectionSlug,
} from '@/entities/OrganizationSection';
import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { Logger } from '@/utils/Logger';
import call from '@/utils/api';
import { schemeParse } from '@/utils/schemeParse';

type PrivateFields = '_activeOrganizationId' | '_loadedOrganizationId' | '_sections';

export class OrganizationSectionsStore implements ILocalStore {
  readonly meta = new MetaStore();

  private _sections: Map<KnownSectionSlug, OrganizationSection> | null = null;
  private _activeOrganizationId = 0;
  private _loadedOrganizationId = 0;

  constructor() {
    makeObservable<OrganizationSectionsStore, PrivateFields>(this, {
      _activeOrganizationId: observable,
      _loadedOrganizationId: observable,
      _sections: observable.ref,
      loadSections: action.bound,
      hasExplicitConfigurationForActiveOrganization: computed,
      enabledSections: computed,
      enabledTopLevelSections: computed,
      enabledDocumentSections: computed,
    });
  }

  get hasExplicitConfigurationForActiveOrganization(): boolean {
    return this._loadedOrganizationId === this._activeOrganizationId && this._sections !== null;
  }

  get enabledSections(): OrganizationSection[] {
    return Array.from(this._getSavedSections()?.values() ?? []);
  }

  get enabledTopLevelSections(): TopLevelSectionSlug[] {
    return this.enabledSections.map((section) => section.slug).filter(isTopLevelSectionSlug);
  }

  get enabledDocumentSections(): DocumentSectionSlug[] {
    return this.enabledSections.map((section) => section.slug).filter(isDocumentSectionSlug);
  }

  async loadSections(organizationId: number): Promise<void> {
    const normalizedOrganizationId = this._normalizeOrganizationId(organizationId);

    this._activeOrganizationId = normalizedOrganizationId;

    if (!normalizedOrganizationId) {
      this._loadedOrganizationId = 0;
      this._sections = null;
      this.meta.reset();

      return;
    }

    if (this._loadedOrganizationId !== normalizedOrganizationId) {
      this._sections = null;
    }

    if (this._loadedOrganizationId === normalizedOrganizationId && this._sections !== null) {
      this.meta.setLoadedSuccessMeta();

      return;
    }

    if (this.meta.isLoading) {
      return;
    }

    this.meta.setLoadedStartMeta();

    const response = await call<{ sections: unknown[] }>({
      endpoint: ENDPOINTS.organizationSections.get(String(normalizedOrganizationId)),
    });

    if (response.isError) {
      this.meta.setLoadedErrorMeta();
      notificationsStore.showError(response.error.response?.data.message ?? 'Не удалось загрузить разделы');

      return;
    }

    const parsedResponse = schemeParse(organizationSectionsResponseSchema, response.data);

    if (!parsedResponse.success) {
      this.meta.setLoadedErrorMeta();
      notificationsStore.showError('Не удалось загрузить разделы');

      return;
    }

    const parsedSections = this._parseSections(parsedResponse.data.sections);

    this._loadedOrganizationId = normalizedOrganizationId;
    this._sections = parsedSections;
    this.meta.setLoadedSuccessMeta();
  }

  isSectionEnabled(slug: string, organizationId?: number): boolean {
    if (!isKnownSectionSlug(slug)) {
      return false;
    }

    const savedSections = this._getSavedSections(organizationId);

    if (!savedSections) {
      return false;
    }

    return savedSections.has(slug);
  }

  getSectionName(slug: KnownSectionSlug, organizationId?: number): string | null {
    const savedSections = this._getSavedSections(organizationId);

    return savedSections?.get(slug)?.name ?? null;
  }

  isChatModelSelectionEnabled(organizationId?: number): boolean {
    if (!this.isSectionEnabled('chat', organizationId)) {
      return false;
    }

    const savedSections = this._getSavedSections(organizationId);

    if (!savedSections) {
      return false;
    }

    return savedSections.get('chat')?.showModelSelection !== false;
  }

  private _parseSections(rawSections: unknown[]): Map<KnownSectionSlug, OrganizationSection> {
    const sections = new Map<KnownSectionSlug, OrganizationSection>();

    rawSections.forEach((rawSection) => {
      const parsedSection = schemeParse(organizationSectionItemSchema, rawSection);

      if (!parsedSection.success) {
        return;
      }

      const normalizedSlug = parsedSection.data.slug.trim();

      if (!isKnownSectionSlug(normalizedSlug)) {
        new Logger('OrganizationSectionsStore').logError(`Unknown section slug: ${normalizedSlug}`);

        return;
      }

      if (sections.has(normalizedSlug) || !parsedSection.data.is_enabled) {
        return;
      }

      const normalizedName = parsedSection.data.name.trim() || SECTION_NAMES[normalizedSlug];

      sections.set(normalizedSlug, {
        slug: normalizedSlug,
        name: normalizedName,
        showModelSelection: parsedSection.data.show_model_selection ?? null,
      });
    });

    return sections;
  }

  private _getSavedSections(organizationId?: number): Map<KnownSectionSlug, OrganizationSection> | undefined {
    const normalizedOrganizationId =
      typeof organizationId === 'number' ? this._normalizeOrganizationId(organizationId) : this._activeOrganizationId;

    if (normalizedOrganizationId !== this._loadedOrganizationId) {
      return undefined;
    }

    return this._sections ?? undefined;
  }

  private _normalizeOrganizationId(organizationId: number): number {
    return Number.isFinite(organizationId) ? Math.max(0, organizationId) : 0;
  }

  destroy(): void {
    this.meta.destroy();
  }
}
